import { useState, useEffect } from 'react';
import { Building2, Search, Check, X, Loader, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Module {
  id: string;
  name: string;
  slug: string;
  description: string;
}

interface UserModule {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  company: string;
  active_module_id: string | null;
  active_module_name: string | null;
}

export function ModulesManagement() {
  const [users, setUsers] = useState<UserModule[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    setLoading(true);
    try {
      const [modulesRes, usersRes] = await Promise.all([
        supabase.from('modules_metiers').select('*').order('name'),
        supabase.rpc('get_all_users_for_admin')
      ]);

      if (modulesRes.error) throw modulesRes.error;
      if (usersRes.error) throw usersRes.error;

      setModules(modulesRes.data || []);

      const usersWithModules = await Promise.all(
        (usersRes.data || []).map(async (user: any) => {
          const { data: activeModule } = await supabase
            .from('client_modules')
            .select('module_id, modules_metiers(id, name)')
            .eq('user_id', user.id)
            .eq('active', true)
            .maybeSingle();

          return {
            id: user.id,
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
            company: user.company,
            active_module_id: activeModule?.module_id || null,
            active_module_name: activeModule?.modules_metiers?.name || null
          };
        })
      );

      setUsers(usersWithModules);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function updateModule(userId: string, moduleId: string) {
    setUpdating(userId);
    try {
      const { data: existingModules, error: fetchError } = await supabase
        .from('client_modules')
        .select('id, module_id')
        .eq('user_id', userId);

      if (fetchError) throw fetchError;

      if (existingModules && existingModules.length > 0) {
        for (const existing of existingModules) {
          await supabase
            .from('client_modules')
            .update({ active: false })
            .eq('id', existing.id);
        }
      }

      const isCurrentModule = existingModules?.some(m => m.module_id === moduleId);

      if (isCurrentModule) {
        await supabase
          .from('client_modules')
          .update({ active: false })
          .eq('user_id', userId)
          .eq('module_id', moduleId);
      } else {
        await supabase
          .from('client_modules')
          .upsert({
            user_id: userId,
            module_id: moduleId,
            active: true,
            activated_at: new Date().toISOString()
          }, {
            onConflict: 'user_id,module_id'
          });
      }

      await fetchData();
      alert('Module mis à jour avec succès');
    } catch (error) {
      console.error('Error updating module:', error);
      alert('Erreur lors de la mise à jour du module');
    } finally {
      setUpdating(null);
    }
  }

  const filteredUsers = users.filter(user => {
    const clientName = user.company ||
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.email;

    return clientName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: users.length,
    withModule: users.filter(u => u.active_module_id).length,
    ...modules.reduce((acc, module) => {
      acc[module.slug] = users.filter(u => u.active_module_id === module.id).length;
      return acc;
    }, {} as Record<string, number>)
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 size={28} />
            Gestion des modules métiers
          </h2>
          <p className="text-gray-600 mt-1">Activer et gérer les modules métiers pour chaque client</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Total clients</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Avec module</div>
          <div className="text-2xl font-bold text-green-600">{stats.withModule}</div>
        </div>
        {modules.map((module, idx) => {
          const colors = [
            'bg-purple-50 border-purple-200 text-purple-600',
            'bg-orange-50 border-orange-200 text-orange-600',
            'bg-cyan-50 border-cyan-200 text-cyan-600'
          ];
          const colorClass = colors[idx % colors.length];
          const [bg, border, text] = colorClass.split(' ');

          return (
            <div key={module.id} className={`${bg} border ${border} rounded-lg p-4`}>
              <div className="text-sm text-gray-600 mb-1">{module.name.replace('Module ', '')}</div>
              <div className={`text-2xl font-bold ${text}`}>{stats[module.slug] || 0}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-yellow-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-gray-900 mb-1">Un seul module actif à la fois</p>
            <p className="text-sm text-gray-700">
              Chaque client ne peut avoir qu'un seul module métier actif simultanément.
              L'activation d'un nouveau module désactive automatiquement le précédent.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 mb-6 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Search size={16} />
          Rechercher un client
        </label>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Nom de l'entreprise ou email..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredUsers.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Module actif</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user) => {
                  const clientName = user.company ||
                    `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                    'Inconnu';

                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{clientName}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-600 text-sm">{user.email}</span>
                      </td>
                      <td className="px-4 py-3">
                        {user.active_module_name ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                            <Check size={14} />
                            {user.active_module_name}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">Aucun module actif</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {modules.map((module) => {
                            const isActive = user.active_module_id === module.id;
                            return (
                              <button
                                key={module.id}
                                onClick={() => updateModule(user.id, module.id)}
                                disabled={updating === user.id}
                                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                                  isActive
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }`}
                                title={module.description}
                              >
                                {updating === user.id ? (
                                  <Loader className="animate-spin" size={14} />
                                ) : isActive ? (
                                  <span className="flex items-center gap-1">
                                    <X size={14} />
                                    Désactiver
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <Check size={14} />
                                    {module.name.replace('Module ', '')}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Building2 className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 text-lg">Aucun client trouvé</p>
          <p className="text-gray-500 text-sm mt-2">Aucun client ne correspond à votre recherche</p>
        </div>
      )}
    </div>
  );
}
